import React, { useState, useMemo } from "react";
import { TwTabs as Tabs, Text, Chip, PieChart } from "@ui-component-library";
import { useMetricData } from "../hooks/UseMetricData";
import { formatNumber } from "../../utils";
import { fixValues } from "./helpers/helpers";
import { usePlusBlockerDialog } from "@components/PlusBlockerDialog/PlusBlockerDialog.context";
import { SubscriptionCoverRow } from "../components/SubscriptionCovers";
import { FellingClassesLegend } from "../../helpers";

export default function MetricVolumeWidget() {
  const { openPlusBlockerDialog } = usePlusBlockerDialog();

  const { totalVolume, TGLdata, fellingClassData, propDfr } = useMetricData();
  const [primaryTabSelected, setPrimaryTabSelected] = useState("tgl");
  const [secondaryTabSelected, setSecondaryTabSelected] = useState("m³sk");
  // TGL data
  const tglTableData = [
    { color: "#D5EE64", data: "Tall", totalPercentage: TGLdata?.tall ?? 0 },
    { color: "#3F8888", data: "Gran", totalPercentage: TGLdata?.gran ?? 0 },
    { color: "#7C6ED9", data: "Löv", totalPercentage: TGLdata?.lov ?? 0 },
  ];
  const totalVolumes = tglTableData.reduce(
    (sum, item) => sum + (item?.totalPercentage ?? 0),
    0
  );

  const tableCalculations = () => {
    const fixedPercentage = fixValues(tglTableData, "totalPercentage", 100);

    const volumesData = fixedPercentage.map((item) => {
      const preciseVolume = (item.totalPercentage * totalVolume) / 100;
      return {
        ...item,
        volume: preciseVolume,
      };
    });

    const fixedVolumeData = fixValues(volumesData, "volume", totalVolume);
    return fixedVolumeData
      .map((item) => {
        const percentageValue = item.totalPercentage;

        let totalValue, unit;
        if (primaryTabSelected === "tgl") {
          if (secondaryTabSelected === "m³sk") {
            totalValue = formatNumber(item.volume);
            unit = "m³sk";
          } else if (secondaryTabSelected === "percentage") {
            totalValue = item.totalPercentage;
            unit = "%";
          }
        }

        return {
          ...item,
          color: item.color,
          percentage: `${percentageValue}%`,
          data: item.data,
          total: totalValue,
          unit: unit,
        };
      })
      .sort((a, b) => {
        const order = ["Tall", "Gran", "Löv"];
        const indexA = order.indexOf(a.data);
        const indexB = order.indexOf(b.data);
        return indexA - indexB;
      });
  };

  const finalTglTableData = useMemo(
    () => tableCalculations(),
    [tglTableData, primaryTabSelected, secondaryTabSelected, totalVolume]
  );

  // FellingClass
  const fellingTableData = (fellingClassData ?? [])
    .map((felling, index) => {
      let color;

      if (FellingClassesLegend[felling.class]) {
        color = FellingClassesLegend[felling.class].color;
      } else {
        color = "#BBBBBB";
      }

      const percentageValue =
        totalVolume > 0 ? (felling.volume / totalVolume) * 100 : 0;
      return {
        color: color,
        data: felling.class,
        totalClassArea: felling.area,
        totalClassVolume: felling.volume,
        areaUnit: "ha",
        volumeUnit: "m³sk",
        percentage: `${percentageValue.toFixed(2)}%`,
      };
    })
    .sort((a, b) => {
      const order = ["S1", "S2", "G1", "G2", "R", "K", "IA"];
      const indexA = order.indexOf(a.data);
      const indexB = order.indexOf(b.data);
      return indexA - indexB;
    });

  //Helpers
  const primaryTabs = [
    { label: "Trädslag", value: "tgl" },
    { label: "Klasser", value: "class" },
    { label: "Diagram", value: "diagram" },
  ];

  const secondaryTabs = [
    { label: "m³sk", value: "m³sk" },
    { label: "%", value: "percentage" },
  ];

  const tableData =
    primaryTabSelected === "tgl"
      ? finalTglTableData
      : primaryTabSelected === "class"
        ? fellingTableData
        : finalTglTableData;

  const renderCustomLegend = () => {
    return (
      <ul className="flex flex-wrap justify-center gap-6 mt-6">
        {finalTglTableData.map((entry, index) => (
          <li key={`item-${index}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-neutral-500">{entry.data}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="py-2 flex justify-between">
        <div className="px-6 flex items-baseline justify-between w-full">
          <div className="pr-6 w-full flex">
            <Text variant="text-xl/leading-none/medium">
              {totalVolume && totalVolume.toLocaleString("sv-SE")}{" "}
              <span>
                <Text variant="text-xs/leading-none/normal"> m³sk </Text>
              </span>
            </Text>
          </div>
          <div className="rounded-xs h-2 flex overflow-hidden w-full">
            {tableData.map((data, index) => {
              return (
                <div
                  key={index}
                  style={{
                    width: data.percentage,
                    backgroundColor: data.color,
                  }}
                  title={`${data.data}: ${data.percentage}`}
                ></div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab section */}
      <div className="py-2 flex justify-between flex-wrap px-2 gap-2">
        <div className="bg-cloud-200 rounded-md items-center justify-center">
          <Tabs
            value={primaryTabSelected}
            tabs={primaryTabs}
            onChange={setPrimaryTabSelected}
          />
        </div>
        {primaryTabSelected === "tgl" && (
          <div className="bg-cloud-200 rounded-md items-center justify-center">
            <Tabs
              value={secondaryTabSelected}
              tabs={secondaryTabs}
              onChange={setSecondaryTabSelected}
            />
          </div>
        )}
      </div>

      {/* Table and Pie chart section */}
      <div className="overflow-y-auto h-[280px]">
        {primaryTabSelected === "tgl" && (
          <table className="table-auto w-full table-fixed">
            <tbody>
              {finalTglTableData.map((data, index) => (
                <tr key={index} className="border-cloud-400 border-b">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="rounded-full h-2 w-2"
                        style={{ backgroundColor: data.color }}
                      />
                      <Text variant="text-sm/leading-none/normal">
                        {data.data}
                      </Text>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <SubscriptionCoverRow
                      shouldBlur={!propDfr}
                      onClick={() => openPlusBlockerDialog("property-data")}
                      containerClassName="flex justify-end"
                    >
                      <Text variant="text-sm/leading-none/normal">
                        {data.total.toLocaleString("sv-SE")}
                        <span className="text-[#737373]"> {data.unit}</span>
                      </Text>
                    </SubscriptionCoverRow>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {primaryTabSelected === "class" && (
          <table className="table-auto w-full">
            <tbody>
              <tr className="border-cloud-400 border-b">
                <td className="px-6 py-4 text-[#737373]">
                  <Text variant="text-xs/leading-none/medium">HKL</Text>
                </td>
                <td className="px-4 py-3 text-[#737373] text-end">
                  <Text variant="text-xs/leading-none/medium">
                    Tot. areal (ha)
                  </Text>
                </td>
                <td className="px-4 py-3 text-[#737373] text-end text-nowrap">
                  <Text variant="text-xs/leading-none/medium">
                    Tot. volym (m³sk)
                  </Text>
                </td>
              </tr>
              {fellingTableData.map((data, index) => (
                <tr key={index} className="border-cloud-400 border-b">
                  <td className="pl-4 py-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="rounded-full h-2 w-2"
                        style={{ backgroundColor: data.color }}
                      />
                      <Text variant="text-sm/leading-none/normal">
                        {data.data}
                      </Text>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <SubscriptionCoverRow
                      shouldBlur={!propDfr}
                      onClick={() => openPlusBlockerDialog("property-value")}
                      containerClassName="flex justify-end"
                      disableIcon={true}
                    >
                      <Text variant="text-sm/leading-none/normal">
                        {data?.totalClassArea ?? 0}
                        <span className="text-[#737373] pl-2">
                          {data.areaUnit}
                        </span>
                      </Text>
                    </SubscriptionCoverRow>
                  </td>
                  <td className="pr-4 py-4">
                    <SubscriptionCoverRow
                      shouldBlur={!propDfr}
                      onClick={() => openPlusBlockerDialog("property-value")}
                      containerClassName="flex justify-end"
                    >
                      <Text variant="text-sm/leading-none/normal">
                        {data.totalClassVolume.toLocaleString("sv-SE")}
                        <span className="text-[#737373] pl-2">
                          {propDfr && data.volumeUnit}
                        </span>
                      </Text>
                    </SubscriptionCoverRow>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {primaryTabSelected === "diagram" && (
          <div className="relative">
            {!propDfr && (
              <Chip
                label="Plus"
                className="bg-[#F6F5FE] text-[#6856C3] fill-[#6856C3] absolute top-4 right-4 z-10"
                icon="SparkleFilled"
              />
            )}

            <PieChart
              className={!propDfr ? "blur-sm" : ""}
              pieChart={{ margin: { top: 0, right: 0, bottom: 0, left: 0 } }}
              pie={{
                data: finalTglTableData?.map((data) => ({
                  name: data.data,
                  percentage: Number(data.totalPercentage),
                  fill: data.color,
                  unit: "%",
                })),
                dataKey: "percentage",
                innerRadius: 60,
                strokeWidth: 5

              }}
              legend={{
                content: renderCustomLegend
              }}
              label={{
                bottom: "m³sk",
                center: totalVolume.toLocaleString("sv-SE"),
                top: "Totalvolym",
              }}
              size={{
                height: 250,
                width: "100%",
              }}
              showLegend={true}
              customLegend={renderCustomLegend}
            />
          </div>
        )}
      </div>
    </div>
  );
}
